/**
 * Keeps the header cart-count-bubble in sync (always visible, including 0).
 */
(function () {
  const HEADER_CART_SELECTOR = '#cart-icon-bubble-new';
  let updateTimer = null;

  function injectStyles() {
    if (document.getElementById('dunns-cart-count-styles')) return;
    const style = document.createElement('style');
    style.id = 'dunns-cart-count-styles';
    style.textContent = `
      #cart-icon-bubble-new .cart-count-bubble,
      #cart-icon-bubble-new .cart-count-bubble[hidden],
      #cart-icon-bubble-new .cart-count-bubble:empty {
        display: flex !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
      #cart-icon-bubble-new .cart-count-bubble {
        background-color: #fff !important;
        color: #000 !important;
      }
      @keyframes dunnsCartIconPop {
        0% { transform: scale(1); }
        28% { transform: scale(1.12); }
        55% { transform: scale(0.96); }
        100% { transform: scale(1); }
      }
      @keyframes dunnsCartBadgeBump {
        0% { transform: scale(1); }
        35% { transform: scale(1.25); }
        100% { transform: scale(1); }
      }
      #cart-icon-bubble-new.dunns-cart-added {
        animation: dunnsCartIconPop 520ms ease;
      }
      #cart-icon-bubble-new.dunns-cart-added .cart-count-bubble {
        animation: dunnsCartBadgeBump 520ms ease;
      }
    `;
    (document.head || document.documentElement).appendChild(style);
  }

  injectStyles();

  function getHeaderCartLink() {
    return document.querySelector(HEADER_CART_SELECTOR);
  }

  function getCountSpan(bubble) {
    if (!bubble) return null;
    return (
      bubble.querySelector('span[aria-hidden="true"]:not(.visually-hidden)') ||
      bubble.querySelector(':scope > span:not(.visually-hidden)')
    );
  }

  function ensureBubble() {
    const link = getHeaderCartLink();
    if (!link) return { link: null, bubble: null, countSpan: null };

    let bubble = link.querySelector('.cart-count-bubble');
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.setAttribute('aria-hidden', 'false');
      const span = document.createElement('span');
      span.setAttribute('aria-hidden', 'true');
      span.textContent = '0';
      bubble.appendChild(span);
      const label = link.querySelector('.header__cart-label');
      link.insertBefore(bubble, label || null);
    }

    return { link, bubble, countSpan: getCountSpan(bubble) };
  }

  function applyCount(cartItemCount) {
    const { bubble, countSpan } = ensureBubble();
    if (!bubble || !countSpan) return;

    const count = Math.max(parseInt(cartItemCount, 10) || 0, 0);
    countSpan.textContent = count < 100 ? String(count) : '99+';
    bubble.removeAttribute('hidden');
    bubble.setAttribute('aria-hidden', 'false');
    bubble.style.removeProperty('display');
    bubble.style.removeProperty('visibility');
  }

  function animateCartAdd() {
    const { link, bubble } = ensureBubble();
    if (!link || !bubble) return;

    link.classList.remove('dunns-cart-added');
    bubble.classList.remove('dunns-cart-added');
    // Restart CSS animation when rapid adds happen.
    void link.offsetWidth;
    link.classList.add('dunns-cart-added');

    setTimeout(() => {
      link.classList.remove('dunns-cart-added');
      bubble.classList.remove('dunns-cart-added');
    }, 650);
  }

  function applyBubbleFromSectionHtml(sectionHtml) {
    const link = getHeaderCartLink();
    if (!link || !sectionHtml) return false;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sectionRoot = doc.querySelector('.shopify-section') || doc.body;
    const sectionIcon = sectionRoot.querySelector('.icon-cart, .icon-cart-empty');
    const headerIcon = link.querySelector('.icon-cart, .icon-cart-empty');

    if (sectionIcon && headerIcon) {
      headerIcon.replaceWith(sectionIcon.cloneNode(true));
    }

    const newBubble = sectionRoot.querySelector('.cart-count-bubble');
    const sectionCountSpan = getCountSpan(newBubble);

    if (newBubble) {
      const existingBubble = link.querySelector('.cart-count-bubble');
      existingBubble
        ? existingBubble.replaceWith(newBubble.cloneNode(true))
        : link.insertBefore(newBubble.cloneNode(true), link.querySelector('.header__cart-label'));
    }

    if (sectionCountSpan) {
      const count = parseInt(sectionCountSpan.textContent, 10);
      if (!isNaN(count)) {
        applyCount(count);
        return true;
      }
    }

    return false;
  }

  function fetchCartCount() {
    return fetch(`/cart.js?ts=${Date.now()}`, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        applyCount(data.item_count);
        return data.item_count;
      })
      .catch(() => null);
  }

  function updateCartCount(cartData, options) {
    const opts = options || {};

    if (cartData?.sections?.['cart-icon-bubble']) {
      if (applyBubbleFromSectionHtml(cartData.sections['cart-icon-bubble'])) {
        const resolvedCount = parseInt(getCountSpan(ensureBubble().bubble)?.textContent || '0', 10);
        if (opts.afterAdd && resolvedCount > 0) animateCartAdd();
        return Promise.resolve(resolvedCount);
      }
    }

    if (cartData && typeof cartData.item_count === 'number') {
      applyCount(cartData.item_count);
      if (opts.afterAdd && cartData.item_count > 0) animateCartAdd();
      return Promise.resolve(cartData.item_count);
    }

    return fetchCartCount().then((count) => {
      if (count === 0 && opts.afterAdd) {
        setTimeout(fetchCartCount, 150);
      }
      if (opts.afterAdd && count > 0) animateCartAdd();
      return count;
    });
  }

  function scheduleCartCountUpdate(cartData, options) {
    clearTimeout(updateTimer);
    updateTimer = setTimeout(() => {
      updateCartCount(cartData, options);
    }, 50);
  }

  window.updateCartCount = updateCartCount;
  window.scheduleCartCountUpdate = scheduleCartCountUpdate;

  function refreshFromPage() {
    injectStyles();
    const { bubble, countSpan } = ensureBubble();
    if (bubble && countSpan && !countSpan.textContent.trim()) {
      applyCount(0);
    }
    fetchCartCount();
  }

  function bindListeners() {
    if (window.__dunnsCartCountBound) return;
    window.__dunnsCartCountBound = true;

    injectStyles();
    refreshFromPage();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === 'cart-count') return;
        scheduleCartCountUpdate(event?.cartData, {
          afterAdd: event?.source === 'product-form',
        });
      });
      subscribe(PUB_SUB_EVENTS.cartError, () => scheduleCartCountUpdate(null, { afterAdd: true }));
    }

    document.addEventListener('cart:updated', () => scheduleCartCountUpdate(null, { afterAdd: true }));
    document.addEventListener('cart:requestComplete', () => scheduleCartCountUpdate(null, { afterAdd: true }));
  }

  window.addEventListener('pageshow', refreshFromPage);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindListeners);
  } else {
    bindListeners();
  }
})();
